import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { allowedCustomerIds, canAccess, canSeeCustomerRef, canMarkAdminOnly, canSeePrivate, canSwitchRegion, customerRegion, effectivePermissions, hasPermission, roleAccess, type AccessVerb, type CustomerClass, type Permission } from "@hisense/shared";
import { useViewRegion } from "@/app/viewRegion";
import { useAuth } from "@/app/auth";
import { menuKeyForPath } from "@/app/nav";

const VERB_OF: Partial<Record<Permission, AccessVerb>> = {
  "records:read": "view",
  "records:create": "create",
  "records:update": "update",
  "records:delete": "delete",
};

/**
 * "Người đang đăng nhập có được làm việc này TRÊN MÀN HÌNH NÀY không?"
 *
 * Thay cho `hasPermission(user.role, …)` rải khắp các trang. Chỗ cũ chỉ biết
 * vai trò, nên một tài khoản được tick "chỉ Xem" ở Khách hàng vẫn thấy nút
 * "Thêm khách hàng" — bấm vào thì máy chủ trả 403. Hàng rào vẫn đúng, nhưng
 * cái nút đó là một lời nói dối với người dùng.
 *
 * Màn hình được suy từ đường dẫn hiện tại, đúng bảng mà máy chủ đang chặn theo.
 * Tài khoản chạy theo vai trò (mặc định của mọi người dùng hiện có) đi nguyên
 * đường cũ, không đổi hành vi.
 *
 * ĐÂY VẪN LÀ TIỆN ÍCH HIỂN THỊ. Máy chủ gác độc lập ở `requireAuth`.
 */
export function useCan(): (perm: Permission) => boolean {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return useCallback(
    (perm: Permission) => {
      if (!user) return false;
      const access = user.access ?? roleAccess(user.role);
      if (access.mode !== "custom") return hasPermission(user.role, perm);
      const verb = VERB_OF[perm];
      const menu = menuKeyForPath(pathname);
      // Quyền không gắn với màn hình nào (xem giá vốn, quản trị hệ thống) đọc
      // từ tập quyền suy ra, không từ ô tick của trang đang mở.
      if (!verb) return effectivePermissions(access).has(perm);
      return !!menu && canAccess(access, menu, verb);
    },
    [user, pathname],
  );
}

/**
 * "Tài khoản này có ô tick nào ở MỘT MENU CỤ THỂ?" — cùng phép tính với `canAccess` ở máy chủ.
 *
 * Khác `useCan`: `useCan` suy menu từ đường dẫn đang mở và nói theo ngôn ngữ `Permission` cũ;
 * hàm này nói thẳng theo ô tick của một menu chỉ định, nên dùng được cả ở màn hình phụ (bảng con,
 * hộp thoại) lẫn khi một trang cần hỏi quyền của menu khác.
 *
 * Gom về một chỗ vì trong đợt đi mười lăm mục vàng (19–20/09/2026) mỗi màn tự viết lại một bản
 * `useXxxAccess` giống hệt nhau; ba bản đó nay gọi vào đây để khỏi trôi mỗi nơi một kiểu.
 */
/**
 * Khu vực dữ liệu của người đang đăng nhập (29/09/2026): "vietnam" / "china", hoặc `null` = cả hai khu vực (quản trị viên luôn
 * `null`). Cùng phép tính với máy chủ — chỉ để điền sẵn và khoá ô Phân loại khách; máy chủ tự chặn độc lập.
 */
export function useDataRegion(): CustomerClass | null {
  const { user } = useAuth();
  const view = useViewRegion();
  if (!user) return null;
  const access = user.access ?? roleAccess(user.role);
  // Đang xem một công ty bằng công tắc ở thanh trên (v1.8 mục 1.1): mọi màn cư xử như tài khoản của công ty đó — bộ lọc Việt Nam /
  // Trung Quốc riêng của từng màn ẩn đi, ô Phân loại khoá theo công ty đang xem.
  return customerRegion({ ...access, viewRegion: view });
}

/** Được chọn xem từng công ty (công tắc ở thanh trên). */
export function useCanSwitchRegion(): boolean {
  const { user } = useAuth();
  return !!user && canSwitchRegion(user.access ?? roleAccess(user.role));
}

/**
 * Thấy và đặt được khách riêng tư / liên hệ riêng tư (30/09/2026) — chỉ quản trị viên, cùng bộ giải với máy chủ (`canSeePrivate`).
 * Chỉ để hiện ô tick và nhãn; máy chủ tự chặn độc lập.
 */
/** Mã hàng của khách trên Sản phẩm (YC01-L1) — máy chủ đã trả rỗng cho người không được thấy; ở đây chỉ để ẩn cột / ô cho gọn. */
export function useCanSeeCustomerRef(): boolean {
  const { user } = useAuth();
  return user ? canSeeCustomerRef(user.access ?? roleAccess(user.role)) : false;
}

export function useCanSeePrivate(): boolean {
  const { user } = useAuth();
  return user ? canSeePrivate(user.access ?? roleAccess(user.role)) : false;
}

/** Được tick "Chỉ quản trị viên" khi lập PI (11/10/2026) — cùng bộ giải với máy chủ (`canMarkAdminOnly`). */
export function useCanMarkAdminOnly(): boolean {
  const { user } = useAuth();
  return user ? canMarkAdminOnly(user.access ?? roleAccess(user.role)) : false;
}

/** Tài khoản chỉ thấy khách mình theo đơn (29/09/2026) — cùng bộ giải với máy chủ (`allowedCustomerIds`). */
export function useFollowedOnly(): boolean {
  const { user } = useAuth();
  return user ? allowedCustomerIds(user.access ?? roleAccess(user.role)) !== null : false;
}

export function useMenuAccess(menu: string): (verb: AccessVerb) => boolean {
  const { user } = useAuth();
  return useCallback(
    (verb: AccessVerb) => {
      if (!user) return false;
      return canAccess(user.access ?? roleAccess(user.role), menu, verb);
    },
    [user, menu],
  );
}
